import { Button, Grid, styled } from "@mui/material";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "../../../redux/root.reducer";
import { IWithClassName, IWithThemeProps } from "../../../theme/default.theme";
import {
    IUserPrefrencesDto,
    UserPreferencesKeys,
} from "../interface/dto/user-preferences.dto";
import { userPreferencesUpdateAction } from "../redux/user-preferences.action";
import ColumnOrder from "./column-order";
import EmailUpdate from "./email-updates";
import FileFormat from "./file-format";
import FileNameFormat from "./filename-format";

const UpdateButton = ({
    onUpdateClick,
    isLoading,
}: {
    onUpdateClick: () => void;
    isLoading: boolean;
}) => (
    <Button
        variant="outlined"
        color="secondary"
        onClick={() => !isLoading && onUpdateClick()}
        disabled={isLoading}
    >
        UPDATE USER PREFERENCES
    </Button>
);

const UserPreferences = ({ className }: IWithClassName) => {
    const dispatch = useDispatch();
    const { data, isLoading } = useSelector(
        (state: IRootState) => state.userPreferences.page,
    );

    const [preferences, setPreferences] = useState<IUserPrefrencesDto>(data);

    useEffect(() => {
        setPreferences(data);
    }, [data]);

    const handleUpdateClick = () =>
        dispatch(userPreferencesUpdateAction(preferences));

    const onChangeHandler = (
        label: UserPreferencesKeys,
        value: Array<string> | string | number | boolean,
    ) =>
        setPreferences((preferences) => ({
            ...preferences,
            [label]: value,
        }));

    return (
        <div className={className}>
            <div className={clsx({ disabled: isLoading })}>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <FileNameFormat
                            saveFileWithExtension={
                                !!preferences.saveFileWithExtension
                            }
                            value={preferences.fileNameFormat}
                            onChange={onChangeHandler}
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FileFormat
                            delimiterValue={preferences.fileFormatDelimiter}
                            terminatorValue={preferences.fileFormatTerminator}
                            onOptionSelect={(value, type) =>
                                onChangeHandler(type, value)
                            }
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <EmailUpdate
                            isLoading={isLoading}
                            emailUpdateFrequency={
                                preferences.emailUpdateFrequency
                            }
                            emailUpdateTime={preferences.emailUpdateTime ?? []}
                            emailUpdateTrackedListsUpdates={
                                preferences.emailUpdateTrackedListsUpdates
                            }
                            onSelect={(value, type) =>
                                onChangeHandler(type, value)
                            }
                        />
                    </Grid>
                </Grid>
                <div>
                    <ColumnOrder
                        isLoading={isLoading}
                        displayHiddenColumns={preferences.displayHiddenColumns}
                        allowCustomColumnOrder={
                            preferences.allowCustomColumnOrder
                        }
                        onSelect={(value, type) => onChangeHandler(type, value)}
                        preferredDataFields={preferences.preferredDataFields}
                    />
                </div>
                <div className="update-button">
                    <UpdateButton
                        onUpdateClick={handleUpdateClick}
                        isLoading={isLoading}
                    />
                </div>
            </div>
        </div>
    );
};

export default styled(UserPreferences)(({ theme }: IWithThemeProps) => ({
    ".MuiSvgIcon-root": {
        height: 16,
    },
    ".update-button": {
        display: "flex",
        justifyContent: "center",
        marginTop: theme?.spacing(6.25),
    },
    ".disabled": {
        opacity: 0.3,
        cursor: "default",
    },
}));
